import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import UserRowSkeleton from '../components/Skeleton/UserRowSkeleton';
import Swal from "sweetalert2";

import { formatCurrency, formatNumber, formatAmount } from "../helpers";
const Users: React.FC = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    uid: '',
    username: '',
    password: '',
    withdrawal_password: '',
  });

  // Debounce: searchTerm change hone ke 400ms baad debouncedSearch update hoga
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // search badlne par page reset
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // API call sirf tab jab debouncedSearch ya page change ho
  useEffect(() => {
    fetchUsersData();
  }, [debouncedSearch, page]);

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/getUserData', {
        params: { page, limit, search: debouncedSearch },
      });
      setUsers(res.data.users || []);
      setTotalUsers(res.data.totalUsers);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user: User) => {
    setEditUserData({
      uid: String(user.id),
      username: user.name,
      password: '',
      withdrawal_password: user.withdrawal_password || '',
    });
    setIsModalOpen(true);
  };

  const handleEditUser = async () => {
    await api.put('/editProfile', {
      uid: editUserData.uid,
      username: editUserData.username,
      password: editUserData.password || undefined,
      withdrawal_password: editUserData.withdrawal_password || undefined,
    });
    Swal.fire({
      icon: "success",
      title: "User Updated",
      text: "User details have been successfully updated.",
    });
    fetchUsersData();
    setIsModalOpen(false);
  };

  const handleBlockUnBlockUser = async (uid: string, isBlocked: boolean) => {
    let block_reason = null;

    if (!isBlocked) {
      const result = await Swal.fire({
        title: "Block User",
        input: "textarea",
        inputLabel: "Reason for blocking",
        inputPlaceholder: "Enter block reason...",
        inputAttributes: { maxlength: 255 },
        showCancelButton: true,
        confirmButtonText: "Block",
        confirmButtonColor: "#d33",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) return "Block reason is required";
        },
      });
      if (!result.isConfirmed) return;
      block_reason = result.value;
    }

    await api.put("/blockUser", {
      uid,
      is_blocked: !isBlocked,
      block_reason,
    });

    fetchUsersData();
    setIsModalOpen(false);
    Swal.fire({
      icon: "success",
      title: `User ${isBlocked ? "unblocked" : "blocked"}`,
      text: `User has been successfully ${isBlocked ? "unblocked" : "blocked"}.`,
    });
  };

    const handleMakeDemo = async (uid, isDemo) => {
    try {

      //are you sure to make collaborator
      const result = await Swal.fire({
        title: "Make Collaborator",
        text: "Are you sure you want to make this user a collaborator?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (!result.isConfirmed) return;

      await api.put("/makeDemoUser", {
        uid,
        is_demo: !isDemo,
      });

      setIsModalOpen(false);
      Swal.fire({
        icon: "success",
        title: `User ${isDemo ? "removed from demo" : "made demo user"}`,
        text: `User has been successfully ${isDemo ? "removed from demo" : "made demo user"}.`,
      });

      fetchUsersData();
    } catch (err) {
      console.log(err)
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-sm text-gray-500">Manage user accounts and their settings.</p>
        </div>
        <span className="text-sm text-gray-500">Total Users: {totalUsers}</span>
      </div>

      {snackbar && (
        <div className="mb-4 rounded bg-green-100 text-green-800 px-4 py-2">
          {snackbar}
        </div>
      )}

      <input
        className="mb-4 w-full max-w-md rounded border px-4 py-2"
        placeholder="Search by name, email or UID"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 truncate">
            <tr>
              {[
                'S.No', 'UID', 'Phone', 'Name', 'Email', 'Password',
                'Withdrawal Password', 'Referral Code', 'Refree',
                'Deposit', 'Withdrawal', 'Balance', 'Actions',
              ].map((h) => (
                <th key={h} className="px-4 py-3 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <UserRowSkeleton columns={12} rows={10} />
            ) : (
              users.map((user, i) => {
                const deposit = user.actual_total_deposit_amount || 0;
                const withdrawal = user.actual_total_withdrawal_amount || 0;
                const balance = user.available_inr || 0;

                return (
                  <tr key={user.id} className="border-t truncate">
                    <td className="px-4 py-2">{(page - 1) * limit + i + 1}</td>
                    <td className="px-4 py-2">{user.id}</td>
                    <td className="px-4 py-2">{user.phone}</td>
                    <td className="px-4 py-2 font-medium">{user.name}</td>
                    <td className="px-4 py-2">{user.email}</td>
                    <td className="px-4 py-2">{user.password2}</td>
                    <td className="px-4 py-2">{user.withdrawal_password || "N/A"}</td>
                    <td className="px-4 py-2">{user.referalcode}</td>
                    <td className="px-4 py-2">{user?.refree || "N/A"}</td>
                    <td className="px-4 py-2 text-green-600">{formatCurrency(deposit)}</td>
                    <td className="px-4 py-2 text-red-600">{formatCurrency(withdrawal)}</td>
                    <td className={`px-4 py-2 font-semibold ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(balance)}
                    </td>
                    <td className="px-4 py-2 space-x-2 flex justify-between items-center">
                      <button
                        onClick={() => handleOpenModal(user)}
                        className="border px-3 py-1 rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => navigate(`/users/profile/${user.id}`)}
                        className="border border-green-300 text-green-600 px-3 py-1 rounded"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleBlockUnBlockUser(user.id, user.is_blocked)}
                        className={`border ${user.is_blocked
                          ? "border-yellow-300 text-yellow-600"
                          : "border-red-300 text-red-600"
                        } px-3 py-1 rounded`}
                      >
                        {user.is_blocked ? "Unblock" : "Block"}
                      </button>
                      {/* make agent button */}
                      <button
                        onClick={() => handleMakeDemo(user.id, user.is_demo)}
                        className={`border ${user.is_demo ? "border-yellow-300 text-yellow-600" : "border-red-300 text-red-600"} px-3 py-1 rounded`}
                      >
                        {user.is_demo ? "Demo User" : "Make Demo User"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span className="px-3 py-1">{page} / {totalPages}</span>
        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <div className="bg-white p-6 rounded w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4">Edit User</h2>
            <input
              className="w-full mb-3 border px-3 py-2"
              value={editUserData.username}
              onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })}
            />
            <input
              type="password"
              placeholder="New password (optional)"
              className="w-full mb-4 border px-3 py-2"
              value={editUserData.password}
              onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })}
            />
            <input
              type="password"
              placeholder="New withdrawal password (optional)"
              className="w-full mb-4 border px-3 py-2"
              value={editUserData.withdrawal_password || ''}
              onChange={(e) => setEditUserData({ ...editUserData, withdrawal_password: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUser}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;